import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typo, Filter, Icon, theme, ListTable, Modal, Loading, Flex } from '../../../ui';
import { useMediaActions } from '../hooks/useMediaActions';
import { MEDIA_TYPES } from '../hooks/constants';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import SearchSelect from '../../../ui/SearchSelect';
import ConfirmDelete from '../../../ui/Modal/ConfirmDelete';

const FilterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const Wrapper = styled.div`
  padding: 1rem 0;
`;

const MediaTable = ({ folders, mediaFiles, openFolder, selectedFolder, setSelectedFilter, selectedFilter, breadcrumbs, isLoading, isError }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { deleteFile, moveFile } = useMediaActions();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [folder, setFolder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaToDelete, setMediaToDelete] = useState(null);

  const handleMediaTypes = useCallback(
    (value) => {
      setSelectedFilter(value);
    },
    [setSelectedFilter]
  );

  const goToFile = useCallback(
    (file) => {
      navigate(`/media-library/${selectedFolder}/${file.id}`);
    },
    [selectedFolder, navigate]
  );

  const formatFileSize = useCallback((size) => {
    if (!size || size <= 0) return '—';
    if (size >= 1024 * 1024 * 1024) return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(2)} kB`;
    return `${size} bytes`;
  }, []);

  const renderIcon = useCallback((type) => {
    if (type === 'dir') return <Icon.Folder color={theme.colors.PRIMARY} />;
    if (type === 'image') return <Icon.Image color={theme.colors.PRIMARY} />;
    if (type === 'video') return <Icon.VideoFile color={theme.colors.PRIMARY} />;
    if (type === 'audio') return <Icon.AudioFile color={theme.colors.PRIMARY} />;
    return <Icon.File color={theme.colors.PRIMARY} />;
  }, []);

  /** Combine folders and media files into a single data array **/
  const data = useMemo(() => {
    return [
      ...folders.map((folder) => ({ ...folder, type: 'dir' })),
      ...mediaFiles.map((file) => ({
        ...file,
        type: file.mime.split('/')[0] /** Extract type from mime (e.g., "image/png" -> "image") **/,
      })),
    ];
  }, [folders, mediaFiles]);

  const openMoveModal = (item) => {
    setIsModalOpen(true);
    setSelectedFile(item);
  };

  /** Define columns for ListTable **/
  const tableInfo = useMemo(
    () => [
      {
        label: t('globals.name'),
        data: (item) => (
          <Flex align={'center'} gap={'10'}>
            {renderIcon(item.type)}
            <span>{item.name}</span>
          </Flex>
        ),
      },
      {
        label: t('globals.type'),
        data: (item) => (item.type === 'dir' ? t('media.folder') : item.type),
      },
      {
        label: t('globals.size'),
        data: (item) => (item.type === 'dir' ? '—' : formatFileSize(item.size)),
      },
      {
        label: t('globals.lastModified'),
        data: (item) => (item.created ? dayjs(item.created).format('DD MMMM YYYY') : '—'),
      },
      {
        label: 'Actions',
        menuOptions: (item) => [
          ...(item.type !== 'dir' ? [{ label: t('globals.move'), onClick: () => openMoveModal(item) }] : []),
          { label: t('globals.delete'), onClick: () => setMediaToDelete(item), variant: 'action' },
        ],
      },
    ],
    [t, renderIcon, formatFileSize]
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    setFolder(null);
  };

  const moveFileToFolder = async () => {
    await moveFile({ directoryId: folder.id, id: selectedFile.id });
    closeModal();
  };

  const handleMediaDelete = async () => {
    await deleteFile({ id: mediaToDelete.id });
    setMediaToDelete(null);
  };

  return (
    <div>
      {/** Filter Section **/}
      <FilterWrapper>
        <Filter info={MEDIA_TYPES} handleFilter={handleMediaTypes} selected={selectedFilter} />
      </FilterWrapper>

      {/** Table Section **/}
      {isLoading ? (
        <Loading />
      ) : isError ? (
        <Typo.Text $textAlign='center'>{t('globals.genericError')}</Typo.Text>
      ) : (
        <ListTable
          fixed={true}
          tableInfo={tableInfo}
          data={data}
          tableName='mediaTable'
          onRowClick={(item) => (item.type === 'dir' ? openFolder(item) : goToFile(item))}
        />
      )}

      <Modal open={isModalOpen} onClose={closeModal} width={'525px'} title={t('media.chooseFolder')} onAccept={moveFileToFolder} showActions>
        <Wrapper>
          <Typo.SmallText>{t('media.selectFolder')}</Typo.SmallText>
          <SearchSelect
            setValue={setFolder}
            selectedOptions={folder}
            id='users'
            options={folders.concat([{ id: 0, name: t('media.rootFolderName') }].filter(() => selectedFile?.parentId)).concat(breadcrumbs.slice(0, -1))}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id || option}>
                  <Typo.Text>{option.name}</Typo.Text>
                </li>
              );
            }}
          />
        </Wrapper>
      </Modal>

      {!!mediaToDelete?.id && (
        <ConfirmDelete
          onClose={setMediaToDelete}
          onAcceptDelete={handleMediaDelete}
          open={!!mediaToDelete?.id}
          description={t('media.deleteTargetConfirmation', { target: mediaToDelete.type === 'dir' ? t('media.folder') : t('media.file') })}
        />
      )}
    </div>
  );
};

export default MediaTable;
